import * as _ from 'underscore';
import { CheckboxState } from '../../common/components/checkboxes/CheckboxState';
import { ListResult } from '../fetchers/ListResult';
import { L10n } from '../L10n/L10n';
import { IApplyCancelButtons } from '../models/IApplyCancelButtons';
import { IServerUserInfo } from '../models/IServerUserInfo';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { ServerUsers } from '../resources/ServerUsers';
import { IGetUsersSiteRolesResult } from '../ServerApi/requests/GetUsersSiteRolesRequest';
import { IUpdateServerUsersSiteMembershipResult } from '../ServerApi/requests/UpdateServerUsersSiteMembershipRequest';
import { IServerUser } from '../ServerApi/types/IServerUser';
import { SiteRoles } from '../ServerApi/types/ISiteRole';
import { IUsersWithSiteRolesModel, SiteRolesPickerClientService } from '../services/SiteRolesPickerClientService';
import { SiteMembershipUpdateActionNotification } from '../services/UserActionNotification';
import { ISiteRoleChanges, SiteRolesPickerCtrl } from './SiteRolesPickerCtrl';

export let SiteRolesEvent = {
  SiteRolesChanged: 'SiteRolesChanged'
};

export interface ISiteMembershipDialogScope extends ng.IScope {
  applyCancelButtons: IApplyCancelButtons;
  busy: boolean;
  changes: ISiteRoleChanges;
  close: (result: any) => void; // from modal scope
  dismiss: (reason?: any) => void; // from modal scope
  editSiteMembershipDescription: string;
  haveSelectedUsers(): boolean;
  users: Set<IServerUser>;
  usersWithSiteRoles: IUsersWithSiteRolesModel;
}

export class SiteMembershipDialogCtrl {

  public static $inject = [
    '$scope',
    '$q',
    '$translate',
    'ServerUsers',
    'SiteMembershipUpdateActionNotification',
    'SiteRolesPickerClientService',
  ];

  constructor(
      private $scope: ISiteMembershipDialogScope,
      private $q: ng.IQService,
              $translate: ng.translate.ITranslateService,
      private serverUsers: ServerUsers,
      private siteMembershipUpdateActionNotification: SiteMembershipUpdateActionNotification,
      private rolesPickerClient: SiteRolesPickerClientService)
  {
    // IApplyCancelButtons
    $scope.applyCancelButtons = {
      applyString:
        $translate.instant('save'),
      cancelString:
        $translate.instant('cancel'),
      apply:
        () => this.apply(),
      cancel:
        () => $scope.dismiss(),
      disableApply:
        () => (!this.isDirty() || $scope.busy),
      disableCancel:
        () => $scope.busy,
      isBusy:
        () => $scope.busy
    };

    const displayName = ($scope.users.size() > 0) ? $scope.users.first().displayName : '';
    $scope.editSiteMembershipDescription =
      $translate.instant(
        'editSiteMembershipDescription',
        { USER_COUNT: $scope.users.size(), USER_NAME: L10n.elideIfLong(displayName) },
        L10n.Interpolations.Count);

    $scope.changes = {
      siteRoles: {},
      serverAdmin: undefined
    };

    $scope.usersWithSiteRoles = null;

    $scope.busy = false;

    $scope.haveSelectedUsers = () => $scope.users.size() > 0;

    // get data for initial state
    this.getSiteRolesForSelectedUsers();
  }

  private apply(): ng.IPromise<any> {
    const scope = this.$scope;

    scope.busy = true;
    const users = _.clone(scope.users); // clone for independence from further selection

    let promise: ng.IPromise<any>;

    if (scope.changes.serverAdmin !== undefined) {
      // server admin changed
      promise = this.updateServerAdmin(users).then(() => {
        if (scope.changes.serverAdmin === CheckboxState.empty && !_.isEmpty(scope.changes.siteRoles)) {
          // server admin got unchecked, site roles changed
          return this.updateSiteMembership(users);
        }
      });
    } else {
      // server admin did not change
      const originalAdminState = SiteRolesPickerCtrl.adminStateFromUsers(this.getServerAdminCount(), scope.users.size());
      if (originalAdminState === CheckboxState.empty && !_.isEmpty(scope.changes.siteRoles)) {
        // server is not checked, site roles changed
        promise = this.updateSiteMembership(users);
      }
    }

    // After all the requests dance, fire events and close the dialog, we're done here.
    return promise.then((result: any) => {
      scope.$emit(SiteRolesEvent.SiteRolesChanged);
      scope.close(result);
    });
  }

  private getServerAdminCount() {
    return this.$scope.usersWithSiteRoles ? this.$scope.usersWithSiteRoles.serverAdmins.length : 0;
  }

  private isDirty(): boolean {
    const scope = this.$scope;
    const originalAdminState = SiteRolesPickerCtrl.adminStateFromUsers(this.getServerAdminCount(), scope.users.size());
    const serverAdminChanged = scope.changes.serverAdmin && (scope.changes.serverAdmin !== originalAdminState);
    const siteRolesChanged = (originalAdminState === CheckboxState.empty && !_.isEmpty(scope.changes.siteRoles));
    return serverAdminChanged || siteRolesChanged;
  }

  // section: update server admin

  private updateServerAdmin(users: Set<IServerUserInfo>): ng.IPromise<any> {
    // users in the set might be stale, so ask the server for the latest admin status
    return this.getServerAdmins(users.keys()).then((serverAdmins: string[]) => {
      let filteredServerUsers: Set<IServerUserInfo>;
      const newAdminState = (this.$scope.changes.serverAdmin === CheckboxState.checked);
      if (newAdminState) {
        // promote non admin users only
        filteredServerUsers = users.filter((user: IServerUserInfo) => !_.contains(serverAdmins, user.id));
      } else {
        // demote admin users only
        filteredServerUsers = users.filter((user: IServerUserInfo) => _.contains(serverAdmins, user.id));
      }
      return this.rolesPickerClient.updateServerAdmin(
        filteredServerUsers,
        newAdminState);
    });
  }

  // section: update site membership or roles

  private updateSiteMembership(users: Set<IServerUserInfo>): ng.IPromise<any> {
    const userIds = users.keys();
    const params = this.rolesPickerClient.siteRoleChangesToParams(userIds, this.$scope.changes.siteRoles);
    return this.rolesPickerClient.updateServerUsersSiteMembership(params).then((result: IUpdateServerUsersSiteMembershipResult) => {

      if(result.results){
        // show toast
        this.siteMembershipUpdateActionNotification.notify(
            result.results,
            users,
            this.$scope.changes.siteRoles);
      }
    });
  }

  // triggered by watch on the users object
  private getSiteRolesForSelectedUsers(): ng.IPromise<any> {
    // return if there are no selected users.
    if (this.$scope.users.size() === 0) {
      return this.$q.when({});
    }

    // get site role data for the selected users.
    const userIds = this.$scope.users.keys();
    return this.serverUsers.getSiteRoles(userIds).then((result: IGetUsersSiteRolesResult) => {
      const newState: IUsersWithSiteRolesModel = {
        users: this.$scope.users,
        userRoles: result.users,
        serverAdmins: []
      };

      // now get server admin data
      // $todo: right now we have to use getServerUsers, ideally we'll have a getServerAdmins API
      return this.getServerAdmins(userIds).then((serverAdmins: string[]) => {
        // now calculate number of server admins since it is not included in getSiteRoles
        newState.serverAdmins = serverAdmins;
        // update this watched model only when we are absolutely ready (ie. server admin state also updated)
        this.$scope.usersWithSiteRoles = newState;
      });
    });
  }

  private getServerAdmins(userIds: string[]): ng.IPromise<string[]> {
    const serverAdmins: string[] = [];
    return this.serverUsers.getByUserIds(userIds).then((usersList: ListResult<IServerUser>) => {
      // now calculate number of server admins since it is not included in the results
      _.each(usersList.items, (user: IServerUser) => {
        if (user.maxSiteRole === SiteRoles.ServerAdministrator) {
          serverAdmins.push(user.id);
        }
      });
      return this.$q.when(serverAdmins);
    });
  }

}

Module.instance().controller('SiteMembershipDialogCtrl', SiteMembershipDialogCtrl);
